import { addBusinessDays, isAfter } from 'date-fns'

import { Feature } from '@ngb-frontend/shared/types'

import { getCountryDateProfile } from './getCountryDateProfile'

import type {
  BusinessRuleContext,
  ResolvedDateRules,
} from '@ngb-frontend/shared/types'

const getMinBusinessDate = (days: number) =>
  addBusinessDays(new Date(), days).toISOString().split('T')[0]

/** Resolve the date/time rules of a journey for the given session context */
export const resolveDateRules = (
  ctx: BusinessRuleContext,
  feature: Feature,
): ResolvedDateRules => {
  const profile = getCountryDateProfile(ctx.countryCode)
  const { leadDays, pickup } = profile
  const autopickSupplier = !!ctx.countryConfig?.autopickPNRSupplier

  const getMinDate = (drivable?: boolean) => {
    switch (feature) {
      case Feature.EoLDCC:
        return getMinBusinessDate(
          drivable ? leadDays.eol.drivable : leadDays.eol.notDrivable,
        )
      case Feature.MnRDCC:
        return getMinBusinessDate(
          ctx.countryConfig?.minBookingDaysAgent ?? leadDays.agent,
        )
      default:
        return getMinBusinessDate(
          ctx.countryConfig?.minBookingDaysDriver ?? leadDays.driver,
        )
    }
  }

  const horizonEnd =
    feature === Feature.EoLDCC ? profile.eolBookingHorizonEnd : undefined

  // PickupAndReturn time rules: the time is fixed unless the country lets the
  // user pick one (Austria). In the driver journey the fixed time only applies
  // when the country also auto-picks the pickup supplier.
  const fixedTime = pickup.userSelectsTime
    ? undefined
    : feature === Feature.MnRDCC
    ? pickup.fixedTime
    : feature === Feature.MnR && autopickSupplier
    ? pickup.fixedTime
    : undefined

  return {
    disabledDates: ctx.disableDateBlocking ? [] : profile.holidays,
    getMinDate,
    ...(horizonEnd && {
      shouldDisableDate: (date: Date) => isAfter(date, new Date(horizonEnd)),
    }),
    ...(feature === Feature.EoLDCC && {
      validate: true,
      timeVariant: 'timeSlot' as const,
    }),
    ...(feature === Feature.MnRDCC && { timeVariant: 'time' as const }),
    pickup: {
      fixedTime,
      hidesTimeChoice: !pickup.userSelectsTime,
      autopickSupplier,
    },
  }
}
